import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Truck, User, Phone, MapPin, Calendar, FileText, ArrowRight, Save } from 'lucide-react';

interface Merchant {
  id: string;
  name: string;
  phone: string;
  address: string;
}

interface Delegate {
  id: number;
  name: string;
}

export function CreatePickupPage() {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [selectedDelegateId, setSelectedDelegateId] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        try {
          const usersRes = await api.get('/api/Users');
          const allUsers = usersRes.data.data?.items || usersRes.data.data || [];

          const fetchedMerchants = (Array.isArray(allUsers) ? allUsers : [])
            .filter((u: any) => u.isActive !== false && u.role?.toLowerCase() === 'merchant')
            .map((u: any) => ({
              id: String(u.id ?? u.userId),
              name: u.name || u.username || '',
              phone: u.phone || '',
              address: u.address || ''
            }));

          setMerchants(fetchedMerchants);
        } catch (err) {
          console.error("Error fetching merchants:", err);
          setMerchants([]);
        }

        try {
          const delegatesRes = await api.get('/api/Delegates');
          const allDelegates = delegatesRes.data.data?.items || delegatesRes.data.data || [];
          setDelegates((Array.isArray(allDelegates) ? allDelegates : [])
            .filter((d: any) => d.isActive !== false)
            .map((d: any) => ({
              id: Number(d.id),
              name: d.name || d.fullName || ''
            })));
        } catch (err) {
          console.error("Error fetching delegates:", err);
          setDelegates([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleMerchantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const merchantId = e.target.value;
    setSelectedMerchantId(merchantId);

    if (!merchantId) {
      setPhone('');
      setAddress('');
      return;
    }

    const merchant = merchants.find(m => m.id === merchantId);

    if (merchant) {
      setPhone(merchant.phone || '');
      setAddress(merchant.address || '');
    } else {
      setPhone('');
      setAddress('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchantId) {
      alert('الرجاء اختيار التاجر');
      return;
    }

    try {
      setSubmitting(true);
      const merchant = merchants.find(m => m.id === selectedMerchantId);

      // ملاحظة: التاجر هنا حساب مستخدم (User) وليس سجل عميل (Customer)،
      // لذلك لا يوجد customerId رقمي مرتبط - نرسل بيانات التواصل كنصوص حرة فقط
      const payload = {
        contactName: merchant ? merchant.name : '',
        contactPhone: phone,
        pickupAddress: address,
        pickupDate: new Date(pickupDate).toISOString(),
        delegateId: selectedDelegateId ? Number(selectedDelegateId) : undefined,
        notes: notes
      };

      await api.post('/api/Pickups', payload);
      alert('تم تسجيل طلب الالتقاط بنجاح!');
      navigate('/pickups');
    } catch (err: any) {
      console.error("Pickup creation error:", err);
      alert(err.response?.data?.message || 'حدث خطأ أثناء حفظ طلب الالتقاط');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto text-right" dir="rtl">
      <div className="flex items-center justify-between mb-6 border-b pb-4">
        <div className="flex items-center gap-2">
          <Truck className="w-6 h-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">إنشاء طلب التقاط جديد (Pickup Request)</h1>
            <p className="text-xs text-gray-500 mt-0.5">تكليف كابتن باستلام شحنات طرود جديدة من مقر التاجر</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/pickups')}
          className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-xl"
        >
          <ArrowRight className="w-4 h-4" /> العودة للطلبات
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500">جاري تحميل بيانات التجار والمناديب...</div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-5">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
                <User className="w-3.5 h-3.5 text-orange-500" /> اسم التاجر الراسل
              </label>
              <select
                required
                value={selectedMerchantId}
                onChange={handleMerchantChange}
                className="w-full p-2.5 border rounded-xl bg-white text-xs font-semibold text-gray-700 outline-none focus:border-orange-500"
              >
                <option value="">-- اختر التاجر من النظام --</option>
                {merchants.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              {selectedMerchantId && !phone && !address && (
                <p className="text-xs text-amber-600 mt-1">تنبيه: هذا التاجر ليس لديه هاتف أو عنوان مسجل، يرجى إدخالهما يدوياً</p>
              )}
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
                <Phone className="w-3.5 h-3.5 text-orange-500" /> رقم الهاتف للاتصال والتنسيق
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500"
                placeholder={phone ? "تم التعبئة تلقائياً من بيانات التاجر" : "أدخل رقم الهاتف يدوياً"}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <MapPin className="w-3.5 h-3.5 text-orange-500" /> عنوان استلام الشحنات (مستودع/مقر التاجر)
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500"
              placeholder={address ? "تم التعبئة تلقائياً من بيانات التاجر" : "أدخل العنوان التفصيلي للاستلام"}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
                <Calendar className="w-3.5 h-3.5 text-orange-500" /> تاريخ موعد الالتقاط
              </label>
              <input
                type="date"
                required
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500 font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
                <Truck className="w-3.5 h-3.5 text-orange-500" /> إسناد لكابتن الاستلام (اختياري)
              </label>
              <select
                value={selectedDelegateId}
                onChange={(e) => setSelectedDelegateId(e.target.value)}
                className="w-full p-2.5 border rounded-xl bg-white text-xs font-semibold text-gray-700 outline-none focus:border-orange-500"
              >
                <option value="">-- إرسال إشعار عام للمناديب / بدون كابتن --</option>
                {delegates.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700 flex items-center gap-1 mb-1">
              <FileText className="w-3.5 h-3.5 text-orange-500" /> ملاحظات إضافية
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-2.5 border rounded-xl text-xs outline-none focus:border-orange-500 resize-none"
              placeholder="اكتب أي مواصفات أو تنبيهات إضافية للمندوب هنا..."
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-orange-500/20 transition-all"
            >
              <Save className="w-4 h-4" /> {submitting ? 'جاري تسجيل الطلب...' : 'تأكيد وحفظ طلب الالتقاط'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
